/**
 * `process/06-REPORT-SCREEN-MAP.md` — hand-authored, gated both directions against
 * `process/03-SCREEN-INVENTORY.md` and §28.2, transcribed verbatim here. **`title` is the join
 * key to the served catalogue row, never the heading** (the map's own words: "the link is this
 * table, never the heading") — `ReportLayer` matches on this exact string.
 */
export const REPORTS: readonly { readonly unitId: string; readonly title: string; readonly notes?: readonly string[] }[] = [
  { unitId: 'v-access', title: 'Access review, who can touch what' },
  {
    unitId: 'v-radmins',
    title: 'Principals holding admin',
    notes: [
      "Admin is the permission that can remove branch protection, and an admin can cancel a running job. There is no MFA column: Bitbucket exposes no per-member two-factor status, and this product's own mfa_enabled describes portal users rather than Bitbucket principals — a different population. Rendering it here would be a fabricated record on a screen whose output is someone losing access.",
    ],
  },
  { unitId: 'v-runused', title: 'Write permission never exercised' },
  {
    unitId: 'v-branch',
    title: 'Branch protection scoring',
    notes: ['FAIL breaks a baseline rule; WARN is a weaker configuration the baseline flags but does not require. The report names the rule and changes nothing.'],
  },
  { unitId: 'v-rnoappr', title: 'Branches with no required approvals' },
  {
    unitId: 'v-rforce',
    title: 'Branches allowing force push',
    notes: ['Rewrites observed is read from ref history, which is git evidence — so that column is never stale, even where the configuration beside it is.'],
  },
  { unitId: 'v-rdefdel', title: 'Default branches allowing deletion' },
  {
    unitId: 'v-dormant',
    title: 'Dormant repositories',
    notes: ['Measured from the most recent commit, pull request or pipeline activity in the job. A dormant repository is still backed up and still restorable.'],
  },
  {
    unitId: 'v-rnopipe',
    title: 'Repositories with no pipeline recorded',
    notes: [
      'This usually means unbuilt rather than merely quiet, and the copy says so rather than implying a fault. A repository whose configuration phase was skipped is listed as not re-observed, never as zero.',
      'Pipelines enabled is three-valued, not yes/no. Bitbucket returns 404 for a repository where Pipelines was never enabled, which is indeterminate rather than a No — so the column reads Yes, No or Indeterminate, and an absence is never rendered as a negative. design/28-report-catalogue.md §28.2, report 11.',
    ],
  },
  {
    unitId: 'v-cicost',
    title: 'Build minute attribution',
    notes: ['Attributed from the pipeline records in each job. A repository whose pipeline data a job did not reach shows the date of its last complete capture.'],
  },
  {
    unitId: 'v-waste',
    title: 'Failed build waste',
    notes: ['Waste is the sum of minutes consumed by pipeline runs whose final state was failed or errored. Runs stopped by a user are counted separately and are not included here.'],
  },
  {
    unitId: 'v-rflaky',
    title: 'Pipelines failing more than half their runs',
    notes: ['Ranked by the minutes each one burns, because a pipeline that fails often and cheaply is a different problem from one that fails often and expensively.'],
  },
  {
    unitId: 'v-storage',
    title: 'Storage footprint by repository',
    notes: ['Held bytes are what this product stores after deduplication, not what Bitbucket reports. A pointer-inherited repository adds no new bytes.'],
  },
  {
    unitId: 'v-rgrowth',
    title: 'Repositories growing faster than 25% a quarter',
    notes: ['This threshold is the condition behind the storage growth above forecast alert, so the report and the alert read one definition rather than two.'],
  },
  {
    unitId: 'v-rbigdl',
    title: 'Download artifacts larger than 500 MB',
    notes: [
      'Usually build outputs that need not be retained in the repository at all. Every artifact is held with a SHA-256 per file; writing one back is not available — the upload endpoint is undocumented and the endpoint is plan-gated, returning 402 on a Free workspace. Two blockers, not one.',
    ],
  },
  {
    unitId: 'v-filetypes',
    title: 'File type breakdown',
    notes: ['Counted from the captured file tree at the head of every branch. The kind is taken from the extension; no file is opened.'],
  },
  {
    unitId: 'v-rbigfiles',
    title: 'Files larger than 100 MB',
    notes: ['100 MB is the size at which git begins to degrade — a stated rationale, not a Bitbucket limit. The platform limit that does exist is 5 GB per LFS file.'],
  },
  {
    unitId: 'v-rbinary',
    title: 'Binary file types in the tree',
    notes: ['Read from the per-file size index, which is a byproduct of work the backup already does — it costs storage rather than REST calls.'],
  },
  {
    unitId: 'v-seats',
    title: 'Inactive seats',
    // The methodology sentence is now carried by the API's own `evidence_statement` (rendered in
    // the strip above these notes) — kept here is only the one sentence that field doesn't cover.
    notes: ['Removing a seat is done in Bitbucket. This product reports and cannot change workspace membership.'],
  },
  {
    unitId: 'v-radminidle',
    title: 'Inactive users holding admin',
    // Fully carried by `evidence_statement` now — was duplicating its own tail sentence here.
  },
  {
    unitId: 'v-rnoauthor',
    title: 'Users with no recorded authorship at all',
    // Fully carried by `evidence_statement` now — was duplicating its own tail sentence here.
  },
] as const
